/**
 * 语义化展会列表路径 → legacy exhibition-{f}-{s}-{g}-{p}-{c}-{t}-{page}/ 所需的 route.params。
 * 支持文档约定的前缀；聚合入口 n-intl / n-cn。
 * - **一级行业 g-**、**二级行业 i-**：优先 `industryEnglishName`/nameEn 首段 → slug；无有效 slug 时用对应 **数字 id**。
 * - **地区**：可按拉丁 slug / **k-token**（见 {@link semanticListingRegionSegmentBody}）。
 * - **兼容**：历史 URL 仍解析 **纯数字 ID**（如 g-16、i-21、t-1137）。
 * 时间与分页段保留数字：**m-{月}**、**p-{页码}**。
 */

import { parseLegacyFairListNumericPath } from '~/utils/fairListLegacyPath'

export interface SemanticResolveContext {
  /** industry/getAll 原始响应（含 children） */
  industryData?: { code?: number; data?: any[] } | null
  /** 国际地区树 type=1 */
  cityDataIntl?: { code?: number; data?: any[] } | null
  /** 国内地区树 type=0 */
  cityDataDomestic?: { code?: number; data?: any[] } | null
  /** 国内 dimension envelope（regionType=0） */
  dimensionDataDomestic?: unknown
  /** 国际 dimension envelope（regionType=1） */
  dimensionDataIntl?: unknown
  /** 国内 dimension 多维索引 */
  dimensionIndexDomestic?: import('~/utils/fairListLegacyDimensionHasData').DimensionCountListIndex | null
  /** 国际 dimension 多维索引 */
  dimensionIndexIntl?: import('~/utils/fairListLegacyDimensionHasData').DimensionCountListIndex | null
}

export interface FairSemanticSynth {
  ok: boolean
  params?: Record<string, string>
}

const legacyDefaults = (): Record<string, string> => ({
  f: '0', // 一级行业 ID
  s: '0', // 二级行业 ID
  g: '9999', // 地区：0=国际，1=国内，>1=洲 ID，9999=全球
  p: '0', // 省份（国内）/ 国家（国际）ID
  c: '0', // 城市 ID
  t: '0', // 月份（1–12）
  page: '1' // 页码
})

/**
 * 旧展会列表路由七段 digit 参数（`exhibition-{f}-{s}-{g}-{p}-{c}-{t}-{page}/` 与 route.params）。
 * @see legacyDefaults 默认值
 */
export interface LegacyFairListNumericRouteParams {
  /** f — 一级行业 ID，0 表示未选 */
  f: number | string
  /** s — 二级行业 ID，0 表示未选 */
  s: number | string
  /** g — 地区：0=国际，1=国内，>1=洲 ID，9999=全球全部 */
  g: number | string
  /** p — 省份（国内）/ 国家（国际）ID，0 表示未选 */
  p: number | string
  /** c — 城市 ID，0 表示未选 */
  c: number | string
  /** t — 月份（1–12），0 表示未选 */
  t: number | string
  /** page — 页码，从 1 开始 */
  page: number | string
}

/** 七段局部覆盖（合成 href/path 时的 patch） */
export type FairListLegacyLegPatch = Partial<LegacyFairListNumericRouteParams>

/** 语义列表路径中的分页段（规范：**p-{n}**） */
export function semanticPageSegment(page: number | string): string {
  const n = Math.max(1, Math.floor(Number(page)) || 1)
  return `p-${n}`
}

/**
 * 规范展会列表 path 分页段：
 * - `/p12` → `/p-12`
 * - `/p-1` → 移除（第 1 页不写 path 分页段）
 */
export function normalizeFairSemanticListPaginationPath(pathname: string): string {
  let path = (pathname || '').split(/[?#]/)[0].replace(/\/+/g, '/')
  path = path.replace(/\/p(\d+)(?=\/|$)/g, '/p-$1')
  path = path.replace(/\/p-1(?=\/|$)/i, '')
  path = path.replace(/\/+/g, '/').replace(/\/+$/, '') || '/all'
  if (path === '/') return '/all'
  return path
}

/** path 是否含需 301 规范的分页段（遗留 `/p1` 或 `/p-1`） */
export function fairSemanticListPathNeedsPaginationNormalize(pathname: string): boolean {
  const raw = (pathname || '').split(/[?#]/)[0].replace(/\/+/g, '/').replace(/\/+$/, '') || '/'
  const canonical = raw === '/' ? '/all' : raw
  return normalizeFairSemanticListPaginationPath(pathname) !== canonical
}

/**
 * 全国无行业/地区语义段时补 **all**；已有 **g-/i-** 或地区段时不补 **all**；页码 >1 时补 **p-{n}**。
 */
export function finalizeFairSemanticListPathSegments(
  segments: string[],
  leg: Partial<Pick<LegacyFairListNumericRouteParams, 'f' | 's' | 'g' | 'p' | 'c' | 'page'>> = {}
): void {
  const f = Math.floor(Number(leg.f)) || 0 // 一级行业
  const s = Math.floor(Number(leg.s)) || 0 // 二级行业
  const g = Number.isFinite(Number(leg.g)) ? Math.floor(Number(leg.g)) : 9999 // 地区
  const p = Math.floor(Number(leg.p)) || 0 // 省份/国家
  const c = Math.floor(Number(leg.c)) || 0 // 城市
  const nationwide = f <= 0 && s <= 0 && g === 9999 && p <= 0 && c <= 0
  const hasRegionSeg = segments.some(seg => /^(n-cn|n-intl|r-|c-|v-|t-|s-|S-)/i.test(seg))
  const hasIndustrySeg = segments.some(seg => /^[gi]-/i.test(seg))
  if (nationwide && !hasRegionSeg && !hasIndustrySeg && !segments.includes('all')) {
    segments.unshift('all')
  }
  const pg = Math.max(1, Math.floor(Number(leg.page)) || 1) // 页码
  if (pg > 1) segments.push(semanticPageSegment(pg))
}

export function joinFairSemanticListPathname(segments: string[]): string {
  if (!segments.length) return '/all'
  return `/${segments.join('/')}`
}

/** 从 pathname 移除全部分页段（筛选项 href 不应含 p-）。 */
export function stripFairSemanticListPaginationFromPath(pathname: string): string {
  let path = (pathname || '').split(/[?#]/)[0].replace(/\/+/g, '/')
  path = path.replace(/\/p-\d+(?=\/|$)/gi, '')
  path = path.replace(/\/p(\d+)(?=\/|$)/gi, '')
  path = path.replace(/\/+/g, '/').replace(/\/+$/, '') || '/all'
  if (path === '/') return '/all'
  return path
}

/** 语义列表 pathname 是否缺少末尾 `/`（需 301） */
export function fairSemanticListPathNeedsTrailingSlash(pathname: string): boolean {
  const pathOnly = (pathname || '').split(/[?#]/)[0].replace(/\/+/g, '/') || '/'
  return !pathOnly.endsWith('/')
}

/** pathname 末尾补 `/`（`?` query 仍接在 slash 之后）。 */
export function fairSemanticListPathWithTrailingSlash(pathWithQuery: string): string {
  if (!pathWithQuery || pathWithQuery.startsWith('javascript:')) return pathWithQuery
  const qIndex = pathWithQuery.indexOf('?')
  const path = qIndex >= 0 ? pathWithQuery.slice(0, qIndex) : pathWithQuery
  const query = qIndex >= 0 ? pathWithQuery.slice(qIndex) : ''
  const normalizedPath = path.endsWith('/') ? path : `${path}/`
  return `${normalizedPath}${query}`
}

/** 从 pathname 解析分页段 `p-{n}` / `pn`；无则 1 */
export function resolveFairListPageFromPath(pathname: string): number {
  const pathOnly = (pathname || '').split(/[?#]/)[0] ?? ''
  for (const seg of pathOnly.split('/').filter(Boolean)) {
    const pm = /^p-(\d+)$/.exec(seg) ?? /^p(\d+)$/.exec(seg)
    if (pm?.[1]) {
      const n = Math.floor(Number(pm[1]))
      if (Number.isFinite(n) && n > 0) return n
    }
  }
  return 1
}

export type FairListDocumentCanonicalRoute = {
  path?: string
  fullPath?: string
  query?: Record<string, unknown>
  params?: Record<string, unknown>
}

/**
 * 列表页 document canonical path：默认页（第 1 页）pathname + 末尾 `/`，不含 query、不含 path 分页段 p-*。
 * 供父页面 useHead 使用——Nuxt Island 内 useHead 不会合并进最终 HTML。
 */
export function buildFairListDocumentCanonicalPath(route: FairListDocumentCanonicalRoute): string {
  const pathOnly = String(route.path || '/all/').split('?')[0].split('#')[0]
  const legacy = parseLegacyFairListNumericPath(pathOnly)
  if (legacy) {
    const semanticPath = synthesizeFairListSemanticPathFromLegacyNumericRoute({ ...legacy, page: 1 })
    return fairSemanticListPathWithTrailingSlash(semanticPath)
  }
  const withoutPagination = stripFairSemanticListPaginationFromPath(pathOnly)
  return fairSemanticListPathWithTrailingSlash(withoutPagination)
}

/**
 * 翻页 document title：第 2 页及以后在原标题前加「第N页_」（兼容历史尾部 `_第N页` 写法）。
 * 例：第2页_矿业展会_2026年矿业展时间表_排期表_门票 -聚展
 */
export function applyFairListPaginatedDocumentTitle(title: string, page: number | string): string {
  const n = Math.floor(Number(page)) || 1
  if (n <= 1 || !title) return title
  const juZhanSuffix = '-聚展'
  const hasSuffix = title.endsWith(juZhanSuffix)
  let base = hasSuffix ? title.slice(0, -juZhanSuffix.length) : title
  base = base.replace(/_第\d+页$/, '')
  return `第${n}页_${base}${hasSuffix ? juZhanSuffix : ''}`
}

/** 列表页完整 canonical / mobile alternate URL */
export function buildFairListDocumentCanonicalFromRoute(
  route: FairListDocumentCanonicalRoute,
  origin: 'www' | 'm' = 'www'
): string {
  const base = origin === 'm' ? 'https://m.jufair.com' : 'https://www.jufair.com'
  return `${base}${buildFairListDocumentCanonicalPath(route)}`
}

/** 筛选项 href：移除 p- 分页段，pathname 末尾补 `/`。 */
export function fairSemanticListFilterHref(pathWithQuery: string): string {
  if (!pathWithQuery || pathWithQuery.startsWith('javascript:')) return pathWithQuery
  const qIndex = pathWithQuery.indexOf('?')
  const path = qIndex >= 0 ? pathWithQuery.slice(0, qIndex) : pathWithQuery
  const query = qIndex >= 0 ? pathWithQuery.slice(qIndex) : ''
  const stripped = stripFairSemanticListPaginationFromPath(path)
  const normalizedPath = stripped.endsWith('/') ? stripped : `${stripped}/`
  return `${normalizedPath}${query}`
}

/** 301 重定向目标（pathname + query）：保留分页段，pathname 末尾补 `/`。 */
export function fairSemanticListRedirectHref(pathWithQuery: string): string {
  return fairSemanticListPathWithTrailingSlash(pathWithQuery)
}

/** 仅 pathname 的 301 重定向（Vue Router `navigateTo({ path })` 用）。 */
export function fairSemanticListRedirectPathname(pathname: string): string {
  if (!pathname) return '/all/'
  const pathOnly = pathname.split('?')[0].split('#')[0] || '/'
  return pathOnly.endsWith('/') ? pathOnly : `${pathOnly}/`
}

/** pathname 是否含指定前缀的语义段（r- / c- / s-）。 */
export function fairSemanticListingPathHasSegment(pathname: string, prefix: 'r' | 'c' | 's'): boolean {
  const re = prefix === 'r' ? /^r-/i : prefix === 'c' ? /^c-/i : /^s-/i
  const segments = (pathname || '').split(/[?#]/)[0].split('/').filter(Boolean)
  return segments.some(seg => re.test(seg))
}

export type FairSemanticRegionSegmentKind =
  | 'n-intl'
  | 'n-cn'
  | 'intl-continent'
  | 'intl-country'
  | 'intl-city'
  | 'domestic-province'
  | 'domestic-city'

/** 从 pathname 提取地区语义段类型（不含 g-/i-/all/p-/m-）。 */
export function fairSemanticListingRegionKindsInPath(pathname: string): FairSemanticRegionSegmentKind[] {
  const segments = (pathname || '').split(/[?#]/)[0].replace(/\/+$/, '').split('/').filter(Boolean)
  const kinds: FairSemanticRegionSegmentKind[] = []
  for (const seg of segments) {
    if (seg === 'n-intl') kinds.push('n-intl')
    else if (seg === 'n-cn') kinds.push('n-cn')
    else if (/^r-/i.test(seg)) kinds.push('intl-continent')
    else if (/^c-/i.test(seg)) kinds.push('intl-country')
    else if (/^[sS]-/.test(seg)) kinds.push('intl-city')
    else if (/^v-/i.test(seg)) kinds.push('domestic-province')
    else if (/^t-/i.test(seg)) kinds.push('domestic-city')
  }
  return kinds
}

/**
 * 地区 path 只能保留最细一级（与筛选项/旧链 301 一致）：
 * - 末段 **r-** 前不得有 **n-intl** 等；
 * - 末段 **c-** 前不得有 **n-**、**r-**、**c-**；
 * - 末段 **s-** 前不得有 **n-**、**r-**、**c-**；
 * - 末段 **t-** 前不得有 **v-**；
 * - 末段 **v-** 前不得有 **n-cn**。
 * 等价于 pathname 中地区语义段至多 1 个。
 */
export function isFairSemanticListingRegionStackValid(pathname: string): boolean {
  return fairSemanticListingRegionKindsInPath(pathname).length <= 1
}

/**
 * all 与 g-/i- 不可同 path 叠放（如 all + g-car-auto-parts 非法，应 404）。
 * 全国聚合用 all 或单独 g-xxx 路径，二者互斥。
 */
export function isFairSemanticListingAllIndustryStackValid(pathname: string): boolean {
  const segments = (pathname || '').split(/[?#]/)[0].replace(/\/+$/, '').split('/').filter(Boolean)
  if (!segments.includes('all')) return true
  return !segments.some(seg => /^[gi]-/i.test(seg))
}

/** 从语义 pathname 移除指定国际地区段（r- 洲 / c- 国家）。 */
export function stripFairSemanticListingPathSegments(
  pathname: string,
  strip: Array<'intlContinent' | 'intlCountry'>
): string {
  const pathOnly = (pathname || '').split(/[?#]/)[0].replace(/\/+$/, '') || '/'
  if (pathOnly === '/' || pathOnly === '/all') return pathOnly === '/' ? '/all' : pathOnly

  const stripR = strip.includes('intlContinent')
  const stripC = strip.includes('intlCountry')
  const kept = pathOnly
    .split('/')
    .filter(Boolean)
    .filter(seg => {
      if (stripR && /^r-/i.test(seg)) return false
      if (stripC && /^c-/i.test(seg)) return false
      return true
    })
  return kept.length ? `/${kept.join('/')}` : '/all'
}

/** pathname 语义段（不含 query；保留 m-、不含 p- 分页段） */
export function fairSemanticListingPathSegments(pathname: string): string[] {
  const pathOnly = (pathname || '').split(/[?#]/)[0].replace(/\/+$/, '') || '/'
  const normalized = pathOnly === '/' ? '/all' : pathOnly
  return normalized.split('/').filter(Boolean).filter(seg => !/^p-\d+$/i.test(seg))
}

/** 去掉 m-{月} 后的语义段，用于白名单判定 */
export function fairSemanticListingBaseSegments(pathname: string): string[] {
  return fairSemanticListingPathSegments(pathname).filter(seg => !/^m-\d+$/i.test(seg))
}

/**
 * 表格内独立月份 path：纯地域 / 纯行业 / 行业+n-cn|n-intl；
 * 行业+具体地区（r/c/s/v/t）→ false，仍用 ?m=
 */
export function canUseDedicatedMonthPath(segments: string[]): boolean {
  const segs = segments.filter(s => s !== 'all')
  const hasG = segs.some(s => /^g-/i.test(s))
  const hasI = segs.some(s => /^i-/i.test(s))
  const hasSpecificGeo = segs.some(s => /^[rcvt]-/i.test(s) || /^[Ss]-/.test(s))
  if ((hasG || hasI) && hasSpecificGeo) return false
  return true
}

/** path 中 m-{月} → 1–12；无则 0 */
export function resolveFairListMonthFromPath(pathname: string): number {
  for (const seg of fairSemanticListingPathSegments(pathname)) {
    const mm = /^m-(\d+)$/.exec(seg)
    if (mm?.[1]) {
      const n = Math.floor(Number(mm[1]))
      if (Number.isFinite(n) && n > 0 && n <= 12) return n
    }
  }
  return 0
}

export function semanticMonthSegment(month: number | string): string {
  const n = Math.max(1, Math.min(12, Math.floor(Number(month)) || 1))
  return `m-${n}`
}

/** 在 segments 末尾追加 m-{月}（页码段由 finalize 单独处理） */
export function appendFairSemanticMonthSegment(segments: string[], month: number | string): void {
  const n = Math.floor(Number(month))
  if (Number.isFinite(n) && n > 0) segments.push(semanticMonthSegment(n))
}

/**
 * 筛选项 href：在目标 segments 上保留月份（白名单 path **m-**；否则 **?m=**）。
 */
export function buildFairSemanticFilterHrefWithMonth(
  segments: string[],
  month: number,
  opts?: { keyword?: string | null }
): string {
  const segs = segments.filter(s => !/^m-\d+$/i.test(s))
  const monthNum = Math.floor(Number(month)) || 0
  const kw = opts?.keyword != null ? String(opts.keyword).trim() : ''

  if (monthNum > 0) {
    if (canUseDedicatedMonthPath(segs)) {
      appendFairSemanticMonthSegment(segs, monthNum)
      return fairSemanticListFilterHref(
        `${joinFairSemanticListPathname(segs)}${kw ? buildFairListHrefQuerySuffix({ keyword: kw }) : ''}`
      )
    }
    return fairSemanticListFilterHref(
      `${joinFairSemanticListPathname(segs)}${buildFairListHrefQuerySuffix({ month: monthNum, keyword: opts?.keyword })}`
    )
  }

  return fairSemanticListFilterHref(
    `${joinFairSemanticListPathname(segs)}${kw ? buildFairListHrefQuerySuffix({ keyword: kw }) : ''}`
  )
}

/** 非白名单 path 含 m- 时应 301 剥到 query ?m= */
export function shouldStripMonthSegmentToQuery(pathname: string): boolean {
  const month = resolveFairListMonthFromPath(pathname)
  if (month <= 0) return false
  return !canUseDedicatedMonthPath(fairSemanticListingBaseSegments(pathname))
}

/** 白名单 path 是否应把 query ?m= 301 到 path m- */
export function shouldMigrateQueryMonthToPath(
  pathname: string,
  query: Record<string, unknown> | undefined
): boolean {
  if (resolveFairListMonthFromPath(pathname) > 0) return false
  const qm = resolveFairListMonthFromRouteQuery(query)
  if (qm <= 0) return false
  return canUseDedicatedMonthPath(fairSemanticListingBaseSegments(pathname))
}

/** 列表 pathname 后的 query：`?m=` 月份 + `keyword`（与 path 中 m- 段二选一，站内链以 query 为准）。 */
export function buildFairListHrefQuerySuffix(opts: {
  month?: number | string | null
  keyword?: string | null
}): string {
  const parts: string[] = []
  const m = Number(opts.month)
  if (Number.isFinite(m) && m > 0) parts.push(`m=${Math.floor(m)}`)
  const kw = opts.keyword != null ? String(opts.keyword).trim() : ''
  if (kw) parts.push(`keyword=${encodeURIComponent(kw)}`)
  return parts.length ? `?${parts.join('&')}` : ''
}

/** 路由 query.m → legacy `t`（月份）；path 已含 m- 时以 query 为准。 */
export function applyFairListMonthFromRouteQuery(params: Record<string, string>, query: Record<string, unknown>): void {
  const month = resolveFairListMonthFromRouteQuery(query)
  if (month > 0) params.t = String(month)
}

/** 从 route.query.m 解析月份（1–12）；无有效值返回 0。 */
export function resolveFairListMonthFromRouteQuery(query: Record<string, unknown> | undefined): number {
  const raw = query?.m
  if (raw == null || raw === '') return 0
  const s = Array.isArray(raw) ? raw[0] : raw
  const monthNum = Math.floor(Number(s))
  return Number.isFinite(monthNum) && monthNum > 0 ? monthNum : 0
}

/** 路由 path m- 或 query.m → legacy 月份 t；path 优先。 */
export function resolveFairListMonthFromRoute(route: {
  path?: string
  query?: Record<string, unknown>
}): number {
  const fromPath = route.path ? resolveFairListMonthFromPath(route.path) : 0
  if (fromPath > 0) return fromPath
  return resolveFairListMonthFromRouteQuery(route.query)
}

/** 从 pathname 剥离 **m-{月}** 段（301 到 **?m=** 用）。 */
export function stripFairSemanticPathMonthSegment(pathname: string): { path: string; month: number } {
  const pathOnly = (pathname || '').split(/[?#]/)[0].replace(/\/+$/, '') || '/'
  const segments = pathOnly.split('/').filter(Boolean)
  let month = 0
  const kept = segments.filter(seg => {
    const mm = /^m-(\d+)$/.exec(seg)
    if (mm?.[1]) {
      month = Math.floor(Number(mm[1])) || 0
      return false
    }
    return true
  })
  const path = kept.length ? `/${kept.join('/')}` : '/all'
  return { path, month }
}

/** 从候选串中提取拉丁字母片段，连成 kebab（供英文名称 / 英文 locale 下的行业地区名）。 */
export function semanticListingLatinSlug(...candidates: unknown[]): string {
  const parts: string[] = []
  for (const raw of candidates) {
    if (typeof raw !== 'string') continue
    const s = raw.trim()
    if (!s) continue
    const chunks = s.match(/[A-Za-z]+/g)
    if (chunks) parts.push(...chunks.map(w => w.toLowerCase()))
  }
  const out = parts.join('-').replace(/-+/g, '-').replace(/^-|-$/g, '')
  return out && !/^\d+$/.test(out) ? out : ''
}

/**
 * 无拉丁可读名时的稳定 token：**仅小写字母**（含前缀 k），避免出现十进制地区/行业 ID。
 */
export function semanticListingStableTokenFromPositiveInt(seed: unknown): string {
  const n0 = Number(seed)
  if (!Number.isFinite(n0) || n0 < 1) return 'kx'
  let n = Math.floor(n0)
  let s = ''
  while (n > 0) {
    const rem = ((n - 1) % 26) + 1
    s = String.fromCharCode(96 + rem) + s
    n = Math.floor((n - 1) / 26)
  }
  return `k${s}`
}

/** {@link semanticListingStableTokenFromPositiveInt} 的逆向（失败返回 null）。 */
export function semanticListingPositiveIntFromStableToken(token: unknown): number | null {
  if (typeof token !== 'string' || !token.startsWith('k')) return null
  const body = token.slice(1)
  if (!body || !/^[a-z]+$/.test(body)) return null
  let n = 0
  for (const ch of body) {
    const v = ch.charCodeAt(0) - 96
    if (v < 1 || v > 26) return null
    n = n * 26 + v
  }
  return n
}

/**
 * 将一级行业英文名规范为 g- 段路径体：小写、连续非 [a-z0-9] 转为单连字符。
 * 结果为空或整段仅为数字时返回 ''（由调用方再使用一级行业数字 id）。
 */
export function semanticListingSlugFromIndustryEnglishName(englishRaw: unknown): string {
  if (typeof englishRaw !== 'string') return ''
  let s = englishRaw.trim().toLowerCase()
  if (!s) return ''
  s = s
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
  if (!s || /^\d+$/.test(s)) return ''
  return s
}

function pickFirstIndustryEnglishSource(item: Record<string, unknown>): string {
  const direct = item.industryCode
  if (typeof direct === 'string' && direct.trim()) return direct.trim()
  const nameEn = (item as any).nameEn
  if (typeof nameEn === 'string' && nameEn.trim()) {
    const first = nameEn.split(',')[0]
    if (typeof first === 'string' && first.trim()) return first.trim()
  }
  return ''
}

/** g- 段体：优先 industryEnglishName/nameEn → slug；无有效 slug 时用一级行业数字 id（与历史 g-{id} 解析一致）。 */
export function semanticListingFirstIndustryBody(item: Record<string, unknown>): string {
  const fromEn = semanticListingSlugFromIndustryEnglishName(pickFirstIndustryEnglishSource(item))
  if (fromEn) return fromEn
  const idNum = Number((item as any).industryId ?? (item as any).id)
  return Number.isFinite(idNum) && idNum > 0 ? String(idNum) : '0'
}

function pickSecondIndustryEnglishSource(item: Record<string, unknown>): string {
  const direct = item.industryCode
  if (typeof direct === 'string' && direct.trim()) return direct.trim()
  const nameEn = (item as any).nameEn
  if (typeof nameEn === 'string' && nameEn.trim()) {
    const first = nameEn.split(',')[0]
    if (typeof first === 'string' && first.trim()) return first.trim()
  }
  return ''
}

/** i- 段体：优先 industryEnglishName/nameEn 首段 → slug；无有效 slug 时用二级行业数字 id（与历史 i-{id} 解析一致）。 */
export function semanticListingSecondIndustryBody(item: Record<string, unknown>): string {
  const fromEn = semanticListingSlugFromIndustryEnglishName(pickSecondIndustryEnglishSource(item))
  if (fromEn) return fromEn
  const idNum = Number((item as any).id ?? (item as any).industryId)
  return Number.isFinite(idNum) && idNum > 0 ? String(idNum) : '0'
}

/**
 * 地区语义段「体」：拉丁 slug 优先，否则 stable k-token；**不出现十进制阿拉伯数字 ID**。
 * 传入接口里的展示名即可（中英文均可；后续可接专用英文字段）。
 */
export function semanticListingRegionSegmentBody(numericId: number, ...nameCandidates: unknown[]): string {
  const latin = semanticListingLatinSlug(...nameCandidates)
  if (latin) return latin
  return semanticListingStableTokenFromPositiveInt(numericId)
}

/**
 * 国内 **t-**（市）路径体：**优先节点 `areaCode`**；无效时 {@link semanticListingRegionSegmentBody}。
 */
export function semanticListingDomesticCitySegmentBody(node: Record<string, unknown>): string {
  const rawCode = typeof (node as any)?.areaCode === 'string' ? (node as any).areaCode.trim() : ''
  if (rawCode) {
    return semanticListingSlugFromIndustryEnglishName(rawCode) || rawCode.toLowerCase()
  }
  const rawId = Number((node as any)?.id)
  const nid = Number.isFinite(rawId) && rawId > 0 ? rawId : 1
  return semanticListingRegionSegmentBody(nid, (node as any)?.regionName, (node as any)?.area, (node as any)?.enName)
}

/**
 * 国内 **v-**（省）路径体：优先 **`areaCode`** slug；与同结构节点的 {@link semanticListingDomesticCitySegmentBody} 规则一致。
 */
export function semanticListingDomesticProvinceSegmentBody(node: Record<string, unknown>): string {
  return semanticListingDomesticCitySegmentBody(node)
}

/** 地区段追加：顺序与列表筛选一致（n-* / r- / c- / s- / v- / t-）。时间与分页由调用方追加。 */
export type SemanticRegionSegmentBodyGetter = (
  variant: 'intlContinent' | 'intlCountry' | 'intlCity' | 'domesticProvince' | 'domesticCity',
  numericId: number
) => string

/**
 * 向语义 pathname 追加**地区段**（不含行业 g-/i-、月份 ?m=、分页 p-）。
 *
 * 与旧路由七段 **g-p-c** 及列表页筛选顺序一致，**至多输出一段**地区语义（不与 r-/c-/v- 叠放多段）。
 *
 * | g (areaNum) | p / c | 输出 |
 * |-------------|-------|------|
 * | 9999 | — | （不追加，全国无地域） |
 * | 1 国内 | p≤0 且 c≤0 | **n-cn** |
 * | 1 国内 | c>0 | **t-{市}**；若传入 {@link SemanticResolveContext} 且该省仅 1 市（直辖市/港澳等）→ **v-{省}** |
 * | 1 国内 | p>0、c≤0 | **v-{省}** |
 * | 0 国际 | p≤0 且 c≤0 | **n-intl** |
 * | >1 国际 | 有洲/国/城 | **r-** → **c-** → **s-**（按 p/c 逐级追加） |
 *
 * @param segments 可变路径段数组，本函数向末尾 push
 * @param areaNum  legacy **g**：1 国内、0 纯国际聚合、>1 洲 id、9999 不限地域
 * @param provinceNum legacy **p**：国内省 id 或国外国家 id
 * @param cityNum legacy **c**：国内/国外城市 id
 * @param getSegmentBody 数值 id → slug 体（areaCode / 拉丁名 / k-token）
 * @param ctx 可选；传入国内城市树时启用「单城市省份 → v-」规则
 */
export function appendFairSemanticListingRegionBodies(
  segments: string[],
  areaNum: number,
  provinceNum: number,
  cityNum: number,
  getSegmentBody: SemanticRegionSegmentBodyGetter,
  ctx?: SemanticResolveContext
): void {
  const a = areaNum
  const p = provinceNum
  const c = cityNum

  if (a === 9999) return
  if (a === 1) {
    if (p <= 0 && c <= 0) segments.push(`n-cn`)
    else if (c > 0) appendDomesticCityOrSingleProvinceSegment(segments, p, c, getSegmentBody, ctx)
    else if (p > 0) segments.push(`v-${getSegmentBody('domesticProvince', p)}`)
  } else if (a === 0 && p <= 0 && c <= 0) {
    segments.push('n-intl')
  } else {
    if (a > 1) segments.push(`r-${getSegmentBody('intlContinent', a)}`)
    if (p > 0) segments.push(`c-${getSegmentBody('intlCountry', p)}`)
    if (c > 0) segments.push(`s-${getSegmentBody('intlCity', c)}`)
  }
}

/**
 * 行业筛选项内链：已选国家/城市时不输出 **r-**；仅洲时用 **r-**（不用 **n-intl**）；纯国际聚合仍 **n-intl**。
 */
export function appendFairSemanticIndustryFilterRegionBodies(
  segments: string[],
  areaNum: number,
  provinceNum: number,
  cityNum: number,
  getSegmentBody: SemanticRegionSegmentBodyGetter,
  ctx?: SemanticResolveContext
): void {
  const a = areaNum
  const p = provinceNum
  const c = cityNum

  if (a === 9999) return
  if (a === 1) {
    if (p <= 0 && c <= 0) segments.push(`n-cn`)
    else if (c > 0) appendDomesticCityOrSingleProvinceSegment(segments, p, c, getSegmentBody, ctx)
    else if (p > 0) segments.push(`v-${getSegmentBody('domesticProvince', p)}`)
  } else if (a === 0 && p <= 0 && c <= 0) {
    segments.push('n-intl')
  } else {
    if (c > 0) {
      if (ctx?.cityDataIntl && shouldUseIntlCountryInsteadOfCity(p, c, ctx.cityDataIntl)) {
        appendIntlCityOrSingleCountrySegment(segments, p, c, getSegmentBody, ctx)
      } else {
        if (p > 0) segments.push(`c-${getSegmentBody('intlCountry', p)}`)
        segments.push(`s-${getSegmentBody('intlCity', c)}`)
      }
    } else if (p > 0) {
      segments.push(`c-${getSegmentBody('intlCountry', p)}`)
    } else if (a > 1 && p <= 0 && c <= 0) {
      segments.push(`r-${getSegmentBody('intlContinent', a)}`)
    }
  }
}

/**
 * 城市筛选项内链：国外仅 **s-** 城市段（不含 **r-** 洲、**c-** 国家）；
 * 该国仅 1 市时输出 **c-** 国家段。国内逻辑不变。
 */
export function appendFairSemanticCityFilterRegionBodies(
  segments: string[],
  areaNum: number,
  provinceNum: number,
  cityNum: number,
  getSegmentBody: SemanticRegionSegmentBodyGetter,
  ctx?: SemanticResolveContext
): void {
  const a = areaNum
  const p = provinceNum
  const c = cityNum

  if (a === 9999) return
  if (a === 1) {
    if (p <= 0 && c <= 0) segments.push(`n-cn`)
    else if (c > 0) appendDomesticCityOrSingleProvinceSegment(segments, p, c, getSegmentBody, ctx)
    else if (p > 0) segments.push(`v-${getSegmentBody('domesticProvince', p)}`)
  } else if (a === 0 && p <= 0 && c <= 0) {
    segments.push('n-intl')
  } else if (c > 0) {
    appendIntlCityOrSingleCountrySegment(segments, p, c, getSegmentBody, ctx)
  } else if (p > 0) {
    segments.push(`c-${getSegmentBody('intlCountry', p)}`)
  } else if (a > 1) {
    segments.push(`r-${getSegmentBody('intlContinent', a)}`)
  }
}

/** 地区节点子列表：接口有的用 areas，有的用 children/cityList */
export function regionChildren(node: any): any[] {
  if (!node || typeof node !== 'object') return []
  const chunks: any[] = []
  if (Array.isArray(node.areas)) chunks.push(...node.areas)
  if (Array.isArray(node.children)) chunks.push(...node.children)
  if (Array.isArray(node.cityList)) chunks.push(...node.cityList)
  return chunks
}

/** 行业 data 常为数组 */
function industryRoots(env: any): any[] | null {
  const d = env?.data
  if (Array.isArray(d)) return d
  if (d != null && Array.isArray(d.records)) return d.records
  return null
}

/** 城市树 roots：data[] 或 data.records（部分网关包装） */
function cityTreeRoots(env: any): any[] | null {
  const d = env?.data
  if (Array.isArray(d)) return d
  if (d != null && Array.isArray(d.records)) return d.records
  return null
}

function subtreeHasId(node: any, targetId: number): boolean {
  if (node == null) return false
  if (Number(node.id) === targetId) return true
  return regionChildren(node).some(ch => subtreeHasId(ch, targetId))
}

/** 二级行业 id → 一级 industryId */
export function resolveParentFirstIndustry(secondIndustryId: number, industryData: any): number | null {
  const list = industryRoots(industryData) || []
  for (const top of list) {
    for (const ch of top.children || []) {
      if (Number(ch.id) === Number(secondIndustryId)) return Number(top.industryId)
    }
  }
  return null
}

/** 国家 id → 所属洲 id（顶层 data 为洲列表） */
export function resolveIntlCountryContinent(countryId: number, cityDataIntl: any): number | null {
  const roots = cityTreeRoots(cityDataIntl) || []
  for (const continent of roots) {
    const kids = regionChildren(continent)
    if (kids.some(ch => subtreeHasId(ch, countryId))) return Number(continent.id)
  }
  return null
}

/** 国外城市 id → 洲 id + 国家 id（DFS；支持 areas/children/cityList） */
export function resolveIntlCityParents(
  cityId: number,
  cityDataIntl: any
): { continentId: number; countryId: number } | null {
  const roots = cityTreeRoots(cityDataIntl) || []

  function dfsFrom(parent: any, chain: any[]): any[] | null {
    for (const n of regionChildren(parent)) {
      const path = [...chain, n]
      if (Number(n.id) === cityId) return path
      const hit = dfsFrom(n, path)
      if (hit) return hit
    }
    return null
  }

  for (const continent of roots) {
    const continentId = Number(continent.id)
    const path = dfsFrom(continent, [continent])
    if (!path || path.length < 3) continue
    const leaf = path[path.length - 1]
    if (Number(leaf.id) !== cityId) continue
    const countryNode = path[path.length - 2]
    return { continentId, countryId: Number(countryNode.id) }
  }
  return null
}

/**
 * 国内城市 id → 省份 id（与展会列表 ExhibitionList 语义一致）。
 * - 顶层可能是「全国 id=1」下挂各省；也可能是直接扁平的省份列表。
 * - 必须把 p 解析成「省 id」，不能只返回占位 1。
 */
export function resolveDomesticProvinceForCity(cityId: number, cityDataDomestic: any): number | null {
  const roots = cityTreeRoots(cityDataDomestic) || []
  if (!roots.length) return null

  /** 单层全国占位 id=1 时，其子节点才是真正的省列表 */
  let provinces: any[] = roots
  if (roots.length === 1 && Number(roots[0]?.id) === 1 && regionChildren(roots[0]).length > 0) {
    provinces = regionChildren(roots[0])
  }

  for (const prov of provinces) {
    if (!prov || Number(prov.id) === cityId) continue
    if (subtreeHasId(prov, cityId)) return Number(prov.id)
  }

  /** 容错：树根即省且无全国壳时在上面已遍历；若仍未命中再在整棵树搜一次（跳过占位全国 id=1） */
  for (const top of roots) {
    const pid = Number(top?.id)
    if (!pid || pid === 1 || pid === cityId) continue
    if (subtreeHasId(top, cityId)) return pid
  }

  return null
}

/** 国内省份 id → 树节点 */
export function resolveDomesticProvinceNodeById(provinceId: number, cityDataDomestic: any): any | null {
  for (const pv of domesticProvinceRoots(cityDataDomestic)) {
    if (Number(pv?.id) === provinceId) return pv
  }
  return null
}

/** 与列表页 provinceCityList.length 一致：省节点下 areas/children/cityList 数量 */
export function countDomesticProvinceCities(provinceId: number, cityDataDomestic: any): number {
  const prov = resolveDomesticProvinceNodeById(provinceId, cityDataDomestic)
  if (!prov) return 0
  return regionChildren(prov).length
}

/**
 * 直辖市/特别行政区等「省内仅 1 市」：语义 URL 用 **v-** 省段，不用 **t-** 市段。
 */
export function shouldUseDomesticProvinceInsteadOfCity(
  provinceNum: number,
  cityNum: number,
  cityDataDomestic: any | undefined | null
): boolean {
  if (!cityDataDomestic || cityNum <= 0) return false
  const provinceId =
    provinceNum > 0 ? provinceNum : resolveDomesticProvinceForCity(cityNum, cityDataDomestic)
  if (provinceId == null) return false
  return countDomesticProvinceCities(provinceId, cityDataDomestic) === 1
}

/**
 * 国内已选城市（c>0）时追加地区段：**t-{市}** 或 **v-{省}**。
 *
 * 当 {@link SemanticResolveContext.cityDataDomestic} 可用且
 * {@link shouldUseDomesticProvinceInsteadOfCity} 为真（该省 `provinceCityList.length === 1`，
 * 如上海/北京/重庆/天津/港澳）时输出 **v-** 省段，否则输出 **t-** 市段。
 * 省 id 优先取 **p**；**p** 未传时由 {@link resolveDomesticProvinceForCity} 反查。
 */
function appendDomesticCityOrSingleProvinceSegment(
  segments: string[],
  provinceNum: number,
  cityNum: number,
  getSegmentBody: SemanticRegionSegmentBodyGetter,
  ctx?: SemanticResolveContext
): void {
  if (ctx?.cityDataDomestic && shouldUseDomesticProvinceInsteadOfCity(provinceNum, cityNum, ctx.cityDataDomestic)) {
    const provinceId =
      provinceNum > 0
        ? provinceNum
        : resolveDomesticProvinceForCity(cityNum, ctx.cityDataDomestic)!
    segments.push(`v-${getSegmentBody('domesticProvince', provinceId)}`)
    return
  }
  segments.push(`t-${getSegmentBody('domesticCity', cityNum)}`)
}

/** 国外国家 id → 树节点（洲下 DFS） */
export function resolveIntlCountryNodeById(countryId: number, cityDataIntl: any): any | null {
  const roots = cityTreeRoots(cityDataIntl) || []
  for (const continent of roots) {
    const hit = dfsFairTreeFindNodeById(continent, countryId)
    if (hit && Number(hit.id) === countryId) return hit
  }
  return null
}

/** 与列表页 provinceCityList.length 一致：国家节点下 areas/children/cityList 数量 */
export function countIntlCountryCities(countryId: number, cityDataIntl: any): number {
  const country = resolveIntlCountryNodeById(countryId, cityDataIntl)
  if (!country) return 0
  return regionChildren(country).length
}

/**
 * 「国内仅 1 市」的国家（如部分小国）：语义 URL 用 **c-** 国家段，不用 **s-** 市段。
 */
export function shouldUseIntlCountryInsteadOfCity(
  provinceNum: number,
  cityNum: number,
  cityDataIntl: any | undefined | null
): boolean {
  if (!cityDataIntl || cityNum <= 0) return false
  const parents = resolveIntlCityParents(cityNum, cityDataIntl)
  if (!parents) return false
  const countryId = provinceNum > 0 ? provinceNum : parents.countryId
  return countIntlCountryCities(countryId, cityDataIntl) === 1
}

/**
 * 国外已选城市（c>0）时追加地区段：**s-{市}** 或 **c-{国}**。
 *
 * 当 {@link SemanticResolveContext.cityDataIntl} 可用且
 * {@link shouldUseIntlCountryInsteadOfCity} 为真（该国 `provinceCityList.length === 1`）时输出 **c-** 国家段。
 */
function appendIntlCityOrSingleCountrySegment(
  segments: string[],
  provinceNum: number,
  cityNum: number,
  getSegmentBody: SemanticRegionSegmentBodyGetter,
  ctx?: SemanticResolveContext
): void {
  if (ctx?.cityDataIntl && shouldUseIntlCountryInsteadOfCity(provinceNum, cityNum, ctx.cityDataIntl)) {
    const countryId =
      provinceNum > 0
        ? provinceNum
        : resolveIntlCityParents(cityNum, ctx.cityDataIntl)!.countryId
    segments.push(`c-${getSegmentBody('intlCountry', countryId)}`)
    return
  }
  segments.push(`s-${getSegmentBody('intlCity', cityNum)}`)
}

/** pathname 是否含国外城市语义段 **s-** */
export function hasIntlCitySegmentInPath(pathOnly: string): boolean {
  const segments = (pathOnly.split(/[?#]/)[0] || '').replace(/\/+$/, '').split('/').filter(Boolean)
  return segments.some(seg => /^s-[\w.-]+$/i.test(seg))
}

/** 已选国外 **s-** 且所属国仅 1 市时，生成去掉 city 的语义 href（旧链 301 由 synthesize 路径直接输出 c-） */
export function buildFairListRedirectHrefStripSingleCityCountry(
  leg: LegacyFairListNumericRouteParams,
  ctx: SemanticResolveContext
): string | null {
  const provinceNum = Math.floor(Number(leg.p)) || 0
  const cityNum = Math.floor(Number(leg.c)) || 0
  if (cityNum <= 0 || !ctx.cityDataIntl) return null
  const parents = resolveIntlCityParents(cityNum, ctx.cityDataIntl)
  if (!parents) return null
  if (!shouldUseIntlCountryInsteadOfCity(provinceNum, cityNum, ctx.cityDataIntl)) return null
  const countryId = provinceNum > 0 ? provinceNum : parents.countryId
  return synthesizeFairListSemanticHrefFromLegacyNumericRoute(
    { ...leg, g: parents.continentId, p: countryId, c: 0 },
    ctx
  )
}

/** pathname 是否含国内城市语义段 **t-** */
export function hasDomesticCitySegmentInPath(pathOnly: string): boolean {
  const segments = (pathOnly.split(/[?#]/)[0] || '').replace(/\/+$/, '').split('/').filter(Boolean)
  return segments.some(seg => /^t-[\w.-]+$/i.test(seg))
}

/** 已选国内 **t-** 且所属省仅 1 市时，生成去掉 city 的语义 href（旧链 301 由 synthesize 路径直接输出 v-） */
export function buildFairListRedirectHrefStripSingleCityProvince(
  leg: LegacyFairListNumericRouteParams,
  ctx: SemanticResolveContext
): string | null {
  const areaNum = Number.isFinite(Number(leg.g)) ? Math.floor(Number(leg.g)) : 9999
  const provinceNum = Math.floor(Number(leg.p)) || 0
  const cityNum = Math.floor(Number(leg.c)) || 0
  if (areaNum !== 1 || cityNum <= 0) return null
  if (!ctx.cityDataDomestic) return null
  if (!shouldUseDomesticProvinceInsteadOfCity(provinceNum, cityNum, ctx.cityDataDomestic)) return null
  const provinceId =
    provinceNum > 0
      ? provinceNum
      : resolveDomesticProvinceForCity(cityNum, ctx.cityDataDomestic)
  if (provinceId == null) return null
  return synthesizeFairListSemanticHrefFromLegacyNumericRoute(
    { ...leg, g: 1, p: provinceId, c: 0 },
    ctx
  )
}

function resolveFirstIndustryBodyToId(body: string, industryOk: boolean, industryRaw: any): number | null {
  if (!industryOk) return null
  const roots = industryRoots(industryRaw) || []
  if (/^\d+$/.test(body)) {
    const n = Number(body)
    if (!roots.some(t => Number(t.industryId) === n)) return null
    return n
  }
  for (const top of roots) {
    if (semanticListingFirstIndustryBody(top as Record<string, unknown>) === body) return Number(top.industryId)
  }
  return null
}

function resolveSecondIndustryBodyToId(body: string, industryOk: boolean, industryRaw: any): number | null {
  if (!industryOk) return null
  if (/^\d+$/.test(body)) return Number(body)
  const roots = industryRoots(industryRaw) || []
  for (const top of roots) {
    for (const ch of top.children || []) {
      if (semanticListingSecondIndustryBody(ch as Record<string, unknown>) === body) return Number(ch.id)
    }
  }
  return null
}

function domesticProvinceRoots(domRaw: any): any[] {
  const roots = cityTreeRoots(domRaw) || []
  if (roots.length === 1 && Number(roots[0]?.id) === 1 && regionChildren(roots[0]).length > 0) {
    return regionChildren(roots[0])
  }
  return roots
}

function dfsDomesticLeavesFirstMatchBody(body: string, node: any): number | null {
  const kids = regionChildren(node)
  if (kids.length === 0) {
    const id = Number(node?.id)
    if (!id) return null
    if (semanticListingDomesticCitySegmentBody(node as Record<string, unknown>) === body) {
      return id
    }
    return null
  }
  for (const ch of kids) {
    const hit = dfsDomesticLeavesFirstMatchBody(body, ch)
    if (hit != null) return hit
  }
  return null
}

function resolveDomesticProvinceIdFromSlugBody(body: string, domOk: boolean, domRaw: any): number | null {
  if (!domOk) return null
  if (/^\d+$/.test(body)) return Number(body)
  for (const pv of domesticProvinceRoots(domRaw)) {
    const id = Number(pv?.id)
    if (id && semanticListingDomesticProvinceSegmentBody(pv as Record<string, unknown>) === body) return id
  }
  return null
}

function resolveDomesticCityIdFromSlugBody(body: string, domOk: boolean, domRaw: any): number | null {
  if (!domOk) return null
  if (/^\d+$/.test(body)) return Number(body)
  for (const pv of domesticProvinceRoots(domRaw)) {
    const hit = dfsDomesticLeavesFirstMatchBody(body, pv)
    if (hit != null) return hit
  }
  return null
}

function dfsIntlLeavesFirstMatchBody(body: string, continentNode: any): number | null {
  const kids = regionChildren(continentNode)
  if (kids.length === 0) return null
  for (const n of kids) {
    const nk = regionChildren(n)
    if (nk.length === 0) {
      const id = Number(n?.id)
      if (!id) continue
      if (semanticListingDomesticCitySegmentBody(n as Record<string, unknown>) === body) return id
    } else {
      const hit = dfsIntlLeavesFirstMatchBody(body, n)
      if (hit != null) return hit
    }
  }
  return null
}

function resolveIntlContinentIdFromSlugBody(body: string, intlOk: boolean, intlRaw: any): number | null {
  if (!intlOk) return null
  if (/^\d+$/.test(body)) return Number(body)
  const roots = cityTreeRoots(intlRaw) || []
  for (const ct of roots) {
    const id = Number(ct?.id)
    if (id && semanticListingDomesticCitySegmentBody(ct as Record<string, unknown>) === body) return id
  }
  return null
}

function resolveIntlCountryIdFromSlugBody(body: string, intlOk: boolean, intlRaw: any): number | null {
  if (!intlOk) return null
  if (/^\d+$/.test(body)) return Number(body)
  const roots = cityTreeRoots(intlRaw) || []
  for (const continent of roots) {
    for (const co of regionChildren(continent)) {
      const cid = Number(co?.id)
      if (!cid || regionChildren(co).length === 0) continue
      if (semanticListingDomesticCitySegmentBody(co as Record<string, unknown>) === body) return cid
    }
  }
  return null
}

function resolveIntlCityIdFromSlugBody(body: string, intlOk: boolean, intlRaw: any): number | null {
  if (!intlOk) return null
  if (/^\d+$/.test(body)) return Number(body)
  const roots = cityTreeRoots(intlRaw) || []
  for (const continent of roots) {
    const hit = dfsIntlLeavesFirstMatchBody(body, continent)
    if (hit != null) return hit
  }
  return null
}

function applyIndustryFirst(idStr: string, out: Record<string, string>) {
  out.f = idStr // f 一级行业
}

function applyIndustrySecond(idStr: string, industryData: any, out: Record<string, string>): boolean {
  out.s = idStr // s 二级行业
  const pid = resolveParentFirstIndustry(Number(idStr), industryData)
  if (pid == null) return false
  out.f = String(pid)
  return true
}

function okIndustryEnvelope(res: any): boolean {
  return industryRoots(res) != null
}

function okCityTreeEnvelope(res: any): boolean {
  return cityTreeRoots(res) != null
}

/**
 * @param pathname route.path（不含 query）
 *
 * @return 最终返回一个 params 对象
 * 如: { ok: true, params: { f: '1', s: '2', g: '0', p: '0', c: '0', page: '1' } }
 * 其中 f/s/g/p/c/t/page 见 {@link LegacyFairListNumericRouteParams}
 */
export function synthesizeFairSemanticListingPath(
  pathname: string,
  ctx: SemanticResolveContext = {}
): FairSemanticSynth {
  const rawPath = pathname ?? ''
  const pathOnly = rawPath.split(/[?#]/)[0] ?? ''
  const segments = pathOnly.replace(/\/+$/, '').split('/').filter(Boolean)
  if (segments.length === 0) return { ok: false }
  if (!isFairSemanticListingRegionStackValid(pathOnly)) return { ok: false }
  if (!isFairSemanticListingAllIndustryStackValid(pathOnly)) return { ok: false }

  const out = legacyDefaults()
  const industryRaw = ctx.industryData
  const intlRaw = ctx.cityDataIntl // 国际
  const domRaw = ctx.cityDataDomestic // 国内

  const industryOk = industryRaw != null && okIndustryEnvelope(industryRaw)
  const intlOk = intlRaw != null && okCityTreeEnvelope(intlRaw)
  const domOk = domRaw != null && okCityTreeEnvelope(domRaw)

  for (const seg of segments) {
    // 逐段解析语义 path → legacy 七段 out（f/s/g/p/c/t/page）
    // 全国聚合入口
    if (seg === 'all') {
      out.g = '9999' // g 全球
      out.p = '0'
      out.c = '0'
      continue
    }
    // 聚合
    if (seg === 'n-intl') {
      out.g = '0' // g 国际
      out.p = '0'
      out.c = '0'
      continue
    }
    if (seg === 'n-cn') {
      out.g = '1' // g 国内
      out.p = '0'
      out.c = '0'
      continue
    }

    // 分页：规范 p-{n}；仍解析遗留 pn（如无连字符 p12）
    let pm = /^p-(\d+)$/.exec(seg)
    if (pm && pm[1] != null) {
      out.page = pm[1] // page 页码
      continue
    }
    pm = /^p(\d+)$/.exec(seg)
    if (pm && pm[1] != null) {
      out.page = pm[1] // page 页码
      continue
    }

    const mm = /^m-(\d+)$/.exec(seg)
    if (mm && mm[1] != null) {
      const mn = Math.floor(Number(mm[1]))
      if (Number.isFinite(mn) && mn > 0) out.t = String(mn) // t 月份
      continue
    }

    const gm = /^g-([\w.-]+)$/i.exec(seg)
    if (gm && gm[1] != null) {
      const id = resolveFirstIndustryBodyToId(gm[1], industryOk, industryRaw)
      if (id == null) return { ok: false }
      applyIndustryFirst(String(id), out)
      continue
    }

    const im = /^i-([\w.-]+)$/i.exec(seg)
    if (im && im[1] != null) {
      const sid = resolveSecondIndustryBodyToId(im[1], industryOk, industryRaw)
      if (sid == null) return { ok: false }
      if (!applyIndustrySecond(String(sid), industryRaw, out)) return { ok: false }
      continue
    }

    const rm = /^r-([\w.-]+)$/i.exec(seg)
    if (rm && rm[1] != null) {
      const cid = resolveIntlContinentIdFromSlugBody(rm[1], intlOk, intlRaw)
      if (cid == null) return { ok: false }
      out.g = String(cid)
      out.p = '0'
      out.c = '0'
      continue
    }

    const cm = /^c-([\w.-]+)$/i.exec(seg)
    if (cm && cm[1] != null) {
      const countryId = resolveIntlCountryIdFromSlugBody(cm[1], intlOk, intlRaw)
      if (countryId == null) return { ok: false }
      const continentId = resolveIntlCountryContinent(countryId, intlRaw)
      if (continentId == null) return { ok: false }
      out.g = String(continentId)
      out.p = String(countryId)
      out.c = '0'
      continue
    }
    // 国外城市
    const sm = /^[Ss]-([\w.-]+)$/.exec(seg)
    if (sm && sm[1] != null) {
      const cityId = resolveIntlCityIdFromSlugBody(sm[1], intlOk, intlRaw)
      if (cityId == null) return { ok: false }
      const loc = resolveIntlCityParents(cityId, intlRaw)
      if (!loc) return { ok: false }
      out.g = String(loc.continentId)
      out.p = String(loc.countryId)
      out.c = String(cityId)
      continue
    }
    // 国内省份
    const vm = /^v-([\w.-]+)$/i.exec(seg)
    if (vm && vm[1] != null) {
      const pid = resolveDomesticProvinceIdFromSlugBody(vm[1], domOk, domRaw)
      if (pid == null) return { ok: false }
      out.g = '1'
      out.p = String(pid)
      out.c = '0'
      continue
    }
    // 国内城市
    const tm = /^t-([\w.-]+)$/i.exec(seg)
    if (tm && tm[1] != null) {
      const tid = resolveDomesticCityIdFromSlugBody(tm[1], domOk, domRaw)
      if (tid == null) return { ok: false }
      const provinceId = resolveDomesticProvinceForCity(tid, domRaw)
      if (provinceId == null) return { ok: false }
      out.g = '1'
      out.p = String(provinceId)
      out.c = String(tid)
      continue
    }

    // slug 形态（字母）等非本次 ID 路由暂不解析
    return { ok: false }
  }

  return { ok: true, params: out }
}

// ---------------------------------------------------------------------------
// 旧路由 exhibition-f-s-g-p-c-t-page → 语义 pathname（站内 301）
// ---------------------------------------------------------------------------

function domesticProvinceRootsForRedirect(domRaw: any): any[] {
  const roots = cityTreeRoots(domRaw) || []
  if (roots.length === 1 && Number(roots[0]?.id) === 1 && regionChildren(roots[0]).length > 0) {
    return regionChildren(roots[0])
  }
  return roots
}

function dfsFairTreeFindNodeById(root: any, id: number): any | null {
  if (!root) return null
  if (Number(root.id) === id) return root
  for (const ch of regionChildren(root)) {
    const hit = dfsFairTreeFindNodeById(ch, id)
    if (hit) return hit
  }
  return null
}

/**
 * 与列表页一致的地区 slug Getter；缺树或缺节点时用 {@link semanticListingRegionSegmentBody} 兜底。
 */
export function semanticListingRegionSlugGetterFromContext(
  ctx: SemanticResolveContext
): SemanticRegionSegmentBodyGetter {
  const intlRoots = cityTreeRoots(ctx.cityDataIntl) || []
  const domEnvelope = ctx.cityDataDomestic

  return (variant: Parameters<SemanticRegionSegmentBodyGetter>[0], numericId: number): string => {
    const nid = numericId
    switch (variant) {
      case 'intlContinent': {
        const n = intlRoots.find((x: any) => Number(x.id) === nid)
        if (n) return semanticListingDomesticCitySegmentBody(n as Record<string, unknown>)
        return semanticListingRegionSegmentBody(nid)
      }
      case 'intlCountry': {
        let node: any
        for (const ct of intlRoots) {
          node = regionChildren(ct).find((x: any) => Number(x.id) === nid)
          if (node) break
        }
        if (node) return semanticListingDomesticCitySegmentBody(node as Record<string, unknown>)
        return semanticListingRegionSegmentBody(nid)
      }
      case 'intlCity': {
        for (const ct of intlRoots) {
          const hit = dfsFairTreeFindNodeById(ct, nid)
          if (!hit || Number(hit.id) !== nid) continue
          return semanticListingDomesticCitySegmentBody(hit as Record<string, unknown>)
        }
        return semanticListingRegionSegmentBody(nid)
      }
      case 'domesticProvince': {
        for (const pv of domesticProvinceRootsForRedirect(domEnvelope)) {
          if (Number(pv?.id) === nid) return semanticListingDomesticProvinceSegmentBody(pv as Record<string, unknown>)
        }
        return semanticListingRegionSegmentBody(nid)
      }
      case 'domesticCity': {
        for (const pv of domesticProvinceRootsForRedirect(domEnvelope)) {
          const hit = dfsFairTreeFindNodeById(pv, nid)
          if (!hit || Number(hit.id) !== nid) continue
          return semanticListingDomesticCitySegmentBody(hit as Record<string, unknown>)
        }
        return semanticListingRegionSegmentBody(nid)
      }
      default:
        return semanticListingRegionSegmentBody(nid)
    }
  }
}

/** 追加 g-/i-：与列表页 **appendIndustrySemanticPrefixSegments** 一致；无接口数据时用数字 legacy。 */
export function appendFairSemanticIndustryFromLegacySegments(
  segments: string[],
  leg: LegacyFairListNumericRouteParams,
  industryRaw?: any
): void {
  const f = Math.floor(Number(leg.f)) || 0
  const s = Math.floor(Number(leg.s)) || 0

  if (s > 0) {
    const roots = industryRoots(industryRaw) || []
    let row: Record<string, unknown> | null = null
    for (const top of roots) {
      for (const ch of top.children || []) {
        if (Number(ch.id) === s) {
          row = ch as Record<string, unknown>
          break
        }
      }
      if (row) break
    }
    segments.push(row ? `i-${semanticListingSecondIndustryBody(row)}` : `i-${s}`)
    return
  }
  if (f > 0) {
    const roots = industryRoots(industryRaw) || []
    const top = roots.find((t: any) => Number(t.industryId) === f)
    segments.push(top ? `g-${semanticListingFirstIndustryBody(top as Record<string, unknown>)}` : `g-${f}`)
  }
}

function legacyDomesticProvinceNode(ctx: SemanticResolveContext, id: number): any | null {
  for (const pv of domesticProvinceRootsForRedirect(ctx.cityDataDomestic)) {
    if (Number(pv?.id) === id) return pv
  }
  return null
}

function legacyDomesticCityNode(ctx: SemanticResolveContext, id: number): any | null {
  for (const pv of domesticProvinceRootsForRedirect(ctx.cityDataDomestic)) {
    const hit = dfsFairTreeFindNodeById(pv, id)
    if (hit && Number(hit.id) === id) return hit
  }
  return null
}

/**
 * 旧 URL 七段 g-p-c 转语义地区段：g=1 且无 p/c 为 **n-cn**；
 * g=1 且 p>0 时按国内省 **v-** / 国外国家 **c-** 树节点区分（如 527→c-ghana，528→v-guangdong）。
 */
export function appendLegacyFairListRegionBodies(
  segments: string[],
  areaNum: number,
  provinceNum: number,
  cityNum: number,
  ctx: SemanticResolveContext
): void {
  const getBody = semanticListingRegionSlugGetterFromContext(ctx)
  const a = areaNum
  const p = provinceNum
  const c = cityNum

  if (a === 9999) return

  if (a === 1 && p <= 0 && c <= 0) {
    segments.push('n-cn')
    return
  }

  if (a === 1 && c > 0) {
    if (legacyDomesticCityNode(ctx, c)) {
      appendDomesticCityOrSingleProvinceSegment(segments, p, c, getBody, ctx)
      return
    }
    appendIntlCityOrSingleCountrySegment(segments, p, c, getBody, ctx)
    return
  }

  if (a === 1 && p > 0) {
    if (legacyDomesticProvinceNode(ctx, p)) {
      segments.push(`v-${getBody('domesticProvince', p)}`)
    } else {
      segments.push(`c-${getBody('intlCountry', p)}`)
    }
    return
  }

  /**
   * 国际：仅最细粒度（城市 **s-** 不含 **c-/r-**；国家 **c-**；仅洲 **r-**），与城市/行业筛选项内链一致。
   */
  appendFairSemanticCityFilterRegionBodies(segments, a, p, c, getBody, ctx)
}

export function synthesizeFairListSemanticPathFromLegacyNumericRoute(
  leg: LegacyFairListNumericRouteParams,
  ctx: SemanticResolveContext = {}
): string {
  const areaNum = Number.isFinite(Number(leg.g)) ? Math.floor(Number(leg.g)) : 9999
  const provinceNum = Number.isFinite(Number(leg.p)) ? Math.floor(Number(leg.p)) : 0
  const cityNum = Number.isFinite(Number(leg.c)) ? Math.floor(Number(leg.c)) : 0
  const pg = Math.max(1, Math.floor(Number(leg.page)) || 1)
  const f = Math.floor(Number(leg.f)) || 0
  const s = Math.floor(Number(leg.s)) || 0

  const nationwideBase = f <= 0 && s <= 0 && areaNum === 9999 && provinceNum <= 0 && cityNum <= 0

  if (nationwideBase) {
    const monthNum = Number.isFinite(Number(leg.t)) ? Math.floor(Number(leg.t)) : 0
    const segments: string[] = ['all']
    if (monthNum > 0 && canUseDedicatedMonthPath(['all'])) {
      appendFairSemanticMonthSegment(segments, monthNum)
    }
    if (pg > 1) segments.push(semanticPageSegment(pg))
    if (segments.length === 1 && segments[0] === 'all') return '/all'
    return `/${segments.join('/')}`
  }

  const segments: string[] = []
  appendFairSemanticIndustryFromLegacySegments(segments, leg, ctx.industryData)
  appendLegacyFairListRegionBodies(segments, areaNum, provinceNum, cityNum, ctx)

  const monthNum = Number.isFinite(Number(leg.t)) ? Math.floor(Number(leg.t)) : 0
  if (monthNum > 0 && canUseDedicatedMonthPath(segments.filter(s => !/^m-\d+$/i.test(s)))) {
    appendFairSemanticMonthSegment(segments, monthNum)
  }

  if (pg > 1) segments.push(semanticPageSegment(pg))

  return `/${segments.join('/')}`
}

/**
 * 中文站语义列表 href：白名单形态月份写入 path **m-**；否则 **?m=** + keyword。
 */
export function synthesizeFairListSemanticHrefFromLegacyNumericRoute(
  leg: LegacyFairListNumericRouteParams,
  ctx: SemanticResolveContext = {},
  opts?: { keyword?: string | null }
): string {
  const monthNum = Number.isFinite(Number(leg.t)) ? Math.floor(Number(leg.t)) : 0
  const pathname = synthesizeFairListSemanticPathFromLegacyNumericRoute({ ...leg, t: 0 }, ctx)
  const baseSegs = pathname.replace(/^\//, '').split('/').filter(Boolean)
  if (monthNum > 0 && canUseDedicatedMonthPath(baseSegs)) {
    const segs = [...baseSegs]
    appendFairSemanticMonthSegment(segs, monthNum)
    const kw = opts?.keyword != null ? String(opts.keyword).trim() : ''
    return fairSemanticListRedirectHref(
      `${joinFairSemanticListPathname(segs)}${kw ? buildFairListHrefQuerySuffix({ keyword: kw }) : ''}`
    )
  }
  return fairSemanticListRedirectHref(
    `${pathname}${buildFairListHrefQuerySuffix({
      month: monthNum > 0 ? monthNum : undefined,
      keyword: opts?.keyword
    })}`
  )
}
